import Vue from 'vue'
import Router from 'vue-router'

import {
  layout,
  route,
} from '@/util/routes'
import { allow } from '@/util/helpers'
import { PERMISSIONS } from '@/enums/permissions'
import store from '../store/index'

Vue.use(Router)

const router = new Router({
  mode: 'history',

  base: process.env.BASE_URL,

  scrollBehavior: (to, from, savedPosition) => {
    if (to.hash) {
      return { selector: to.hash }
    }
    if (savedPosition) {
      return savedPosition
    }

    return { x: 0, y: 0 }
  },

  routes: [
    layout('Default', [
      route('Dashboard', null, '/'),

      route('UserProfile', null, 'profile'),
      route('Configuracoes', null, 'configuracoes', { permission: PERMISSIONS.CONFIG }),

      route('Clientes', null, 'clientes', { permission: PERMISSIONS.CUSTOMER_VIEW }),
      route('Usuarios', null, 'usuarios', { permission: PERMISSIONS.USER_VIEW }),
      route('Produtos', null, 'produtos', { permission: PERMISSIONS.PRODUCT_VIEW }),
      route('Servicos', null, 'servicos', { permission: PERMISSIONS.SERVICE_VIEW }),
      route('Contratos', null, 'contratos', { permission: PERMISSIONS.CONTRACT_VIEW }),
      route('Fornecedores', null, 'fornecedores', { permission: PERMISSIONS.PROVIDER_VIEW }),

      route('CrmOportunidades', null, 'crm/oportunidades', { permission: PERMISSIONS.CRM_OPPORTUNITY_VIEW }),
      route('Pedidos', null, 'pedidos', { permission: PERMISSIONS.REQUEST_VIEW }),
      route('Pedidos', null, 'ordensServico', { permission: PERMISSIONS.REQUEST_VIEW }, 'Ordens de serviço'),
      route('Compras', null, 'compras', { permission: PERMISSIONS.PURCHASE_VIEW }),
      route('Fiscal', null, 'fiscal', { permission: PERMISSIONS.FISCAL_VIEW }),
      route('Pagamentos', null, 'pagamentos', { permission: PERMISSIONS.REQUEST_VIEW }),
      route('AssistenteInteligente', null, 'assistente-inteligente', { globalOnly: true }),

      route('Receitas', null, 'receitas', { permission: PERMISSIONS.REVENUE_VIEW }),
      route('Despesas', null, 'despesas', { permission: PERMISSIONS.EXPENSE_VIEW }),
      route('Contas', null, 'contas', { permission: PERMISSIONS.ACCOUNT_VIEW }),
      route('ConciliacaoBancaria', null, 'conciliacao-bancaria', { permission: PERMISSIONS.BANK_RECONCILIATION_VIEW }),

      route('Agenda', null, 'agenda'),
      route('Tarefas', null, 'tarefas'),
      route('Metas', null, 'metas', { permission: PERMISSIONS.GOAL_VIEW }),
      route('Sincronizacao', null, 'sync'),

      route('Atividades', null, 'activity'),

      route('Ajuda', null, 'help'),
      route('Pagamento', null, 'billing'),

      route('Artigo', null, 'artigo/:slug'),
      route('PagamentoSucesso', null, 'paymentSucces'),

      route('ReportBasic', null, 'relatorios/cadastros', { permission: PERMISSIONS.REPBASIC }),
      route('ReportRequest', null, 'relatorios/pedidos', { permission: PERMISSIONS.REPREQUEST }),
      route('ReportPurchase', null, 'relatorios/compras', { permission: PERMISSIONS.REPPURCHASE }),
      route('ReportFinancial', null, 'relatorios/financeiro', { permission: PERMISSIONS.REPFINANCIAL }),
      route('ReportManage', null, 'relatorios/gerencial', { permission: PERMISSIONS.REPMANAGE }),
    ]),

    layout('NoLayout', [
      route('Login', null, 'login', { public: true }),
      route('Register', null, 'register', { public: true }),

      route('ForgotPassword', null, 'forgotPassword', { public: true }),
      // route('ResetPassword', null, 'forgotPassword/:id/:token'),

      route('TrackRequest', null, 'trackRequest/:token', { public: true }),
      route('ShareDocuments', null, 'share/:token', { public: true, hideFooter: true }),

      route('TermosDeServico', null, 'termos-de-servico', { public: true }),
      route('PoliticaDePrivacidade', null, 'politica-de-privacidade', { public: true }, 'Política de Privacidade'),
      route('PricePolicy', { default: 'PoliticaDePrivacidade' }, 'pricePolicy', { public: true }, 'Política de Privacidade'),
      route('Maintenance', { default: 'Maintenance' }, 'maintenance', { public: true }, 'Manutencao'),
      route('AcessoBloqueado', null, 'acesso-bloqueado'),

      route('NotFound', null, '*', { public: true }),
    ]),
  ],
})

const isAccessBlocked = accessControl => {
  if (!accessControl) {
    return false
  }
  return accessControl.accessAllowed === false || ['BLOCKED', 'MANUAL_BLOCKED'].includes(String(accessControl.status || '').toUpperCase())
}

const ACCESS_CONTROL_ALLOWED_ROUTES = new Set(['AcessoBloqueado', 'Login'])

router.beforeEach(async (to, from, next) => {
  if (to.meta.public) {
    return next()
  }

  let user = store.get('authStore@currentUser')
  if (!user?.token) {
    try {
      user = await store.dispatch('authStore/restoreSession')
    } catch (error) {
      if (!error.response) {
        return next({ name: 'Manutencao', query: { from: to.fullPath } })
      }
      return next({ name: 'Login' })
    }
  }
  if (!user?.token) {
    return next({ name: 'Login' })
  }

  if (to.meta.globalOnly && !user?.isGlobal) {
    return next({ name: 'Dashboard' })
  }

  const initialized = store.get('app@initialized')
  if (!initialized) {
    await store.dispatch('app/init', user)
  }

  const userPermissions = store.get('authStore@userPermissions') || []
  if (to.meta.permission && !allow(to.meta.permission, userPermissions)) {
    return next({ name: 'Dashboard' })
  }

  const accessControl = store.get('app@accessControl')
  const blocked = isAccessBlocked(accessControl)

  if (blocked && !ACCESS_CONTROL_ALLOWED_ROUTES.has(to.name)) {
    return next({ name: 'AcessoBloqueado' })
  }

  if (!blocked && to.name === 'AcessoBloqueado') {
    return next({ name: 'Dashboard' })
  }

  next()
})

router.afterEach((to) => {
  const base = 'Campag ERP'

  // Usa o nome da rota como título
  if (to.name) {
    document.title = `${base} • ${to.name}`
  } else {
    document.title = base
  }
})

export default router
